import type { Profile, Shipment, Subscription, UsageSeries } from "api";
import type { PaneKey } from "components/Nav/nav.model";
import { arrivalOf, carrierLogoUrl, daysUntil, isDelivered, statusTag } from "components/Shipments/shipments.model";
import type { Product } from "constants/products.constants";
import { findProductByTool } from "constants/products.constants";
import { formatShortDate } from "utils/date.utils";
import { formatCount } from "utils/number.utils";

/** Where a card sends you when it is clicked. */
export type GotoTarget = {
  pane: PaneKey;
  /** Left out for a pane that opens on nothing in particular. */
  sub?: string;
};

export type KpiTone = "ok" | "bad";

export type Kpi = {
  key: string;
  label: string;
  value: string;
  /** The figure's own context: what the number is made of. */
  suffix?: string;
  hint: string;
  hintTone?: KpiTone;
  goto: GotoTarget;
};

export type AttentionTone = "bad" | "warn" | "plain";

export type AttentionItem = {
  id: string;
  tone: AttentionTone;
  title: string;
  detail: string;
  action: { label: string; goto: GotoTarget };
};

export type ArrivalRow = {
  id: number;
  reference: string;
  route: string;
  cube: string;
  tag: { label: string; modifier: string | null };
  /** How far along, 0–100. Null when the departure date is unknown. */
  progress: number | null;
};

export type UsageMeter = {
  key: string;
  label: string;
  used: number;
  limit: number | null;
  percent: number;
  text: string;
};

/** Shipments are what most cards send you to, so it gets a name of its own. */
const MY_SHIPMENTS: GotoTarget = { pane: "shipments", sub: "shipments" };
/** My Qubics lists every tool the company holds, with its limit and usage. */
const SUBSCRIPTIONS: GotoTarget = { pane: "myqubics" };

/** Cards a dashboard list shows before it stops being a summary. */
const ATTENTION_LIMIT = 4;
const ARRIVALS_LIMIT = 5;

/** The horizon the arrivals card names in its own title. */
export const ARRIVALS_WINDOW_DAYS = 7;

/** A subscription this close to its cap is worth saying out loud. */
const USAGE_WARNING_PERCENT = 80;

/** An end date this near is worth saying out loud too. */
const EXPIRY_WARNING_DAYS = 30;

const active = (shipments: Shipment[]): Shipment[] => shipments.filter((shipment) => !isDelivered(shipment));

/** "IN_TRANSIT" reads as "in transit" in the middle of a sentence. */
const readableStatus = (status: string | null): string =>
  status === null ? "unknown" : status.toLowerCase().replace(/_/g, " ");

/**
 * "3 in transit, 1 planned" — what a group of shipments is actually doing,
 * biggest group first. Empty for an empty group.
 */
const statusBreakdown = (shipments: Shipment[]): string => {
  const counts = new Map<string, number>();

  shipments.forEach((shipment) => {
    const status = readableStatus(shipment.status);

    counts.set(status, (counts.get(status) ?? 0) + 1);
  });

  return [...counts.entries()]
    .sort(([, a], [, b]) => b - a)
    .map(([status, count]) => `${count} ${status}`)
    .join(", ");
};

/** Whole days a shipment is past its arrival date. Zero when it is not. */
const daysLate = (shipment: Shipment): number => {
  const days = daysUntil(arrivalOf(shipment));

  return days === null || days >= 0 ? 0 : -days;
};

const isLate = (shipment: Shipment): boolean => !isDelivered(shipment) && daysLate(shipment) > 0;

const arrivesWithin = (shipment: Shipment, days: number): boolean => {
  if (isDelivered(shipment)) return false;

  const until = daysUntil(arrivalOf(shipment));

  return until !== null && until >= 0 && until <= days;
};

/**
 * Good morning / afternoon / evening, by the viewer's own clock. The name is
 * dropped rather than replaced when the profile has none — "Good morning," with
 * a blank after it reads worse than no name at all.
 */
export const greeting = (profile: Profile | null): string => {
  const hour = new Date().getHours();
  const partOfDay = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  const name = profile?.first_name?.trim();

  return name ? `Good ${partOfDay}, ${name}` : `Good ${partOfDay}`;
};

/**
 * The line under the greeting: what this account currently amounts to. Only
 * the parts that have something to say are included.
 */
export const summaryLine = (shipments: Shipment[], subscriptions: Subscription[]): string => {
  const tracked = active(shipments).length;
  const products = subscriptions.filter((subscription) => subscription.status === "active").length;

  const parts = [
    tracked === 1 ? "1 shipment tracked" : `${formatCount(tracked)} shipments tracked`,
    products === 1 ? "1 product active" : `${products} products active`,
  ];

  return parts.join(" · ");
};

/** Calls used this month across every subscription, and the cap if there is one. */
const monthlyCalls = (subscriptions: Subscription[]): { used: number; limit: number | null } => {
  const used = subscriptions.reduce((total, subscription) => total + subscription.usage.used, 0);
  // One unlimited tool makes the total unlimited: a cap that does not cover
  // everything is not the account's cap. No subscriptions at all is not a cap
  // of zero either — `every` is true on an empty list, which would read "0/0".
  const capped = subscriptions.length > 0 && subscriptions.every((s) => s.monthly_limit !== null);

  return {
    used,
    limit: capped ? subscriptions.reduce((total, s) => total + (s.monthly_limit ?? 0), 0) : null,
  };
};

/** The first of next month, which is when every monthly counter restarts. */
const nextReset = (): string => {
  const now = new Date();

  return formatShortDate(new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 1)).toISOString().slice(0, 10));
};

export const kpis = (shipments: Shipment[], subscriptions: Subscription[]): Kpi[] => {
  const running = active(shipments);
  const sea = running.filter((shipment) => shipment.shipment_type !== "AIR").length;
  const air = running.length - sea;

  const soon = running.filter((shipment) => arrivesWithin(shipment, 3));
  const late = shipments.filter(isLate);

  const averageDelay =
    late.length === 0 ? 0 : late.reduce((total, shipment) => total + daysLate(shipment), 0) / late.length;

  const calls = monthlyCalls(subscriptions);

  return [
    {
      key: "active",
      label: "Active shipments",
      value: formatCount(running.length),
      hint: running.length === 0 ? "Nothing in transit" : `${sea} sea · ${air} air`,
      goto: MY_SHIPMENTS,
    },
    {
      key: "soon",
      label: "Arriving in ≤ 3 days",
      value: formatCount(soon.length),
      hint: soon.length === 0 ? "Nothing due this week" : statusBreakdown(soon),
      hintTone: soon.length > 0 ? "ok" : undefined,
      goto: MY_SHIPMENTS,
    },
    {
      key: "late",
      label: "Delayed",
      value: formatCount(late.length),
      hint: late.length === 0 ? "Everything on schedule" : `avg. ${averageDelay.toFixed(1)} days behind ETA`,
      hintTone: late.length > 0 ? "bad" : undefined,
      goto: MY_SHIPMENTS,
    },
    {
      key: "calls",
      label: "API calls this month",
      value: formatCount(calls.used),
      suffix: calls.limit === null ? undefined : `/${formatCount(calls.limit)}`,
      hint: subscriptions.length === 0 ? "No subscriptions yet" : `resets ${nextReset()}`,
      goto: SUBSCRIPTIONS,
    },
  ];
};

/**
 * What the account should look at, worst first: delays, then subscriptions
 * running out of calls or of time, then cards the carrier has not answered on.
 *
 * Only real triggers make the list — a dashboard that manufactures things to
 * worry about stops being read.
 */
export const attentionItems = (shipments: Shipment[], subscriptions: Subscription[]): AttentionItem[] => {
  const items: AttentionItem[] = [];

  [...shipments]
    .filter(isLate)
    .sort((a, b) => daysLate(b) - daysLate(a))
    .forEach((shipment) => {
      const late = daysLate(shipment);
      const route = [shipment.location.from, shipment.location.to].filter(Boolean).join(" → ");

      items.push({
        id: `late-${shipment.id}`,
        tone: "bad",
        title: `${shipment.reference} delayed ${late} ${late === 1 ? "day" : "days"}`,
        detail: [readableStatus(shipment.status), route, `ETA ${formatShortDate(arrivalOf(shipment))}`]
          .filter(Boolean)
          .join(" · "),
        action: { label: "Open shipment", goto: MY_SHIPMENTS },
      });
    });

  subscriptions.forEach((subscription) => {
    const product = findProductByTool(subscription.service_tool);
    const name = product?.name ?? subscription.label;
    const { usage, monthly_limit: limit } = subscription;

    if (subscription.status === "limit_reached") {
      items.push({
        id: `limit-${subscription.service_tool}`,
        tone: "bad",
        title: `${name} has reached its monthly limit`,
        detail:
          limit === null
            ? `${formatCount(usage.used)} calls used this month`
            : `${formatCount(usage.used)} of ${formatCount(limit)} calls used — calls are refused until the counter resets`,
        action: { label: "Subscriptions", goto: SUBSCRIPTIONS },
      });

      return;
    }

    if (usage.percent !== null && usage.percent >= USAGE_WARNING_PERCENT && limit !== null) {
      items.push({
        id: `usage-${subscription.service_tool}`,
        tone: "warn",
        title: `${name} is at ${usage.percent}% of its monthly calls`,
        detail: `${formatCount(usage.used)} of ${formatCount(limit)} used · ${formatCount(usage.remaining ?? 0)} left`,
        action: { label: "Subscriptions", goto: SUBSCRIPTIONS },
      });
    }
  });

  subscriptions.forEach((subscription) => {
    const days = daysUntil(subscription.expires_at);

    if (days === null || days < 0 || days > EXPIRY_WARNING_DAYS) return;

    const name = findProductByTool(subscription.service_tool)?.name ?? subscription.label;

    items.push({
      id: `expiry-${subscription.service_tool}`,
      tone: "warn",
      title: `${name} expires ${formatShortDate(subscription.expires_at)}`,
      detail: days === 0 ? "The subscription ends today" : `${days} ${days === 1 ? "day" : "days"} left on it`,
      action: { label: "Subscriptions", goto: SUBSCRIPTIONS },
    });
  });

  const silent = active(shipments).filter((shipment) => arrivalOf(shipment) === null);

  if (silent.length > 0) {
    items.push({
      id: "silent",
      tone: "plain",
      title: `${silent.length} ${silent.length === 1 ? "shipment is" : "shipments are"} awaiting a first event`,
      detail: "The carrier has not reported on them yet — no route or ETA to show",
      action: { label: "Open shipments", goto: MY_SHIPMENTS },
    });
  }

  // Severity decides the order, not the order the sources were walked in: a
  // tool that has stopped answering must not sit under one that is merely busy.
  const severity: Record<AttentionTone, number> = { bad: 0, warn: 1, plain: 2 };

  return items.sort((a, b) => severity[a.tone] - severity[b.tone]).slice(0, ATTENTION_LIMIT);
};

/**
 * How far along a shipment is, as the design's track draws it. Null without a
 * departure date: a dot placed at a guess is worse than no track.
 */
const progressOf = (shipment: Shipment): number | null => {
  const departure = shipment.date.from;
  const arrival = arrivalOf(shipment);

  if (departure === null || arrival === null) return null;

  const start = new Date(departure).getTime();
  const end = new Date(arrival).getTime();

  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;

  const share = ((Date.now() - start) / (end - start)) * 100;

  // Never pinned to either end: the dots there are the ports themselves.
  return Math.min(96, Math.max(4, Math.round(share)));
};

/**
 * What lands inside the window, soonest first — which puts anything already
 * overdue at the top, since its ETA is in the past. Overdue cards stay on the
 * list however far behind they are: the card is about what has not arrived.
 */
export const arrivals = (shipments: Shipment[]): ArrivalRow[] =>
  active(shipments)
    .filter((shipment) => {
      const until = daysUntil(arrivalOf(shipment));

      return until !== null && until <= ARRIVALS_WINDOW_DAYS;
    })
    .sort((a, b) => new Date(arrivalOf(a) ?? "").getTime() - new Date(arrivalOf(b) ?? "").getTime())
    .slice(0, ARRIVALS_LIMIT)
    .map((shipment) => ({
      id: shipment.id,
      reference: shipment.reference,
      route: [shipment.location.from, shipment.location.to].filter(Boolean).join(" → ") || "Route pending",
      cube: carrierLogoUrl(shipment),
      tag: statusTag(shipment),
      progress: progressOf(shipment),
    }));

/**
 * One meter per subscription, fullest first — the design's "Plan usage" card.
 */
export const usageMeters = (subscriptions: Subscription[]): UsageMeter[] =>
  subscriptions
    .map((subscription) => {
      const { usage, monthly_limit: limit } = subscription;
      const label = findProductByTool(subscription.service_tool)?.name ?? subscription.label;

      return {
        key: subscription.service_tool,
        label,
        used: usage.used,
        limit,
        percent: Math.min(100, Math.max(0, usage.percent ?? 0)),
        text:
          limit === null ? `${formatCount(usage.used)} calls` : `${formatCount(usage.used)} / ${formatCount(limit)}`,
      };
    })
    .sort((a, b) => b.percent - a.percent);

/** The products this company actually has, in the catalogue's own order. */
export const activeProducts = (subscriptions: Subscription[]): Product[] =>
  subscriptions
    .map((subscription) => findProductByTool(subscription.service_tool))
    .filter((product): product is Product => product !== undefined);

/**
 * Bar heights for the call chart, as a share of the busiest day. A window with
 * no calls at all draws nothing rather than a row of full-height bars.
 */
export const chartBars = (usage: UsageSeries | null): { date: string; used: number; height: number }[] => {
  if (usage === null) return [];

  const peak = Math.max(...usage.series.map((day) => day.used), 0);

  return usage.series.map((day) => ({
    date: day.date,
    used: day.used,
    // A day with calls always shows something, or a quiet day and a busy one
    // look the same next to a spike.
    height: peak === 0 ? 0 : Math.max(day.used === 0 ? 0 : 4, Math.round((day.used / peak) * 100)),
  }));
};
